/**
 * StorageAdapter — abstraction over localStorage (web) / Capacitor Preferences (native)
 *
 * All persistent key-value storage in the app must go through this adapter.
 * When Capacitor is added, swap the implementation here to use:
 *   import { Preferences } from '@capacitor/preferences';
 *   await Preferences.set({ key, value });
 *   const { value } = await Preferences.get({ key });
 *   await Preferences.remove({ key });
 *
 * The interface is intentionally synchronous to match the existing localStorage
 * usage pattern. When migrating to Capacitor Preferences (async), update callers
 * to await these methods and change return types accordingly.
 */

export const StorageAdapter = {
  getItem(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  },

  setItem(key: string, value: string): void {
    try {
      localStorage.setItem(key, value);
    } catch {
      // Storage quota exceeded or unavailable — fail silently
    }
  },

  removeItem(key: string): void {
    try {
      localStorage.removeItem(key);
    } catch {
      // Fail silently
    }
  },

  clear(): void {
    try {
      localStorage.clear();
    } catch {
      // Fail silently
    }
  },
};
